/**
 * RevenueManagement.js — Admin only
 * List of all Revenue records
 * Admin can update: received_amount, date_of_receipt, mode_of_receipt
 * Also shows Invoice details if available
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiTrendingUp, FiSearch, FiRefreshCw, FiEye,
  FiEdit2, FiCheck, FiX, FiXCircle, FiCalendar, FiDollarSign,
  FiFileText, FiSave,
} from 'react-icons/fi';
import DateInput from '../components/DateInput';
import * as api from '@crm/shared/api';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';

/* ─── Colors ─── */
const C = {
  navy:'#0D3B5E', navyL:'#EBF4FF',
  gold:'#F5A623', goldL:'#FEF3DC',
  green:'#16A34A', greenL:'#DCFCE7', greenB:'#86EFAC',
  red:'#DC2626',  redL:'#FEF2F2',  redB:'#FECACA',
  teal:'#0D9488', tealL:'#CCFBF1',
  border:'#E5E7EB', borderL:'#F3F4F6', bg:'#F4F6F9',
  white:'#FFFFFF', t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

const fmtDate = (s) => {
  if (!s) return '—';
  try {
    const d = new Date(s);
    const y = String(d.getFullYear()).slice(-2);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${y}`;
  } catch { return s; }
};
const fmtAmt = n => n ? '₹' + Number(n).toLocaleString('en-IN') : '—';
const toISO = (dmy) => {
  if (!dmy) return '';
  // dd/mm/yyyy → yyyy-mm-dd
  const parts = dmy.split('/');
  if (parts.length !== 3) return dmy;
  const [d, m, y] = parts;
  return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
};
const fromISO = (iso) => {
  if (!iso) return '';
  // yyyy-mm-dd → dd/mm/yyyy
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

/* ─── Shimmer ─── */
const Sh = () => (
  <div style={{ height:60, borderRadius:8, background:'#F1F5F9', marginBottom:8,
    backgroundImage:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)',
    backgroundSize:'400px 100%', animation:'cfShimmer 1.4s infinite' }}/>
);
const Spin = () => (
  <div style={{ width:13, height:13, borderRadius:'50%',
    border:'2px solid rgba(255,255,255,0.35)', borderTopColor:'#fff',
    animation:'cfSpin 0.7s linear infinite', flexShrink:0 }}/>
);

const INPUT = {
  height:36, border:`1.5px solid ${C.border}`, borderRadius:8, padding:'0 10px',
  fontSize:13, color:C.t1, background:'#FAFBFC', width:'100%',
  boxSizing:'border-box', fontFamily:'inherit', outline:'none',
};

const MODE_OPTIONS = [
  { value:'', label:'— Select —' },
  { value:'NEFT',   label:'NEFT'   },
  { value:'RTGS',   label:'RTGS'   },
  { value:'CHEQUE', label:'Cheque' },
  { value:'CASH',   label:'Cash'   },
  { value:'UPI',    label:'UPI'    },
];

/* ════════════════════════════════════════════
   INLINE EDIT ROW
════════════════════════════════════════════ */
function EditableRow({ revenue, lead, onSaved, navigate }) {
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);

  // Auto-calculated payout from backend (lead_detail.auto_payout_to_company)
  // Falls back to payout_to_company if already set
  const autoPayout = Number(
    revenue?.lead_detail?.auto_payout_to_company ||
    revenue?.payout_to_company || 0
  );

  const [form, setForm] = useState({
    received_amount: revenue.received_amount
      ? String(revenue.received_amount)
      : (autoPayout > 0 ? String(autoPayout) : ''),
    date_of_receipt: fromISO(revenue.date_of_receipt) || '',
    mode_of_receipt: revenue.mode_of_receipt || '',
  });

  // Re-sync form when revenue prop updates (after save/re-fetch) or autoPayout resolves
  // Only when not actively editing — never overwrite user's in-progress input
  useEffect(() => {
    if (!editing) {
      setForm({
        received_amount: revenue.received_amount
          ? String(revenue.received_amount)
          : (autoPayout > 0 ? String(autoPayout) : ''),
        date_of_receipt: fromISO(revenue.date_of_receipt) || '',
        mode_of_receipt: revenue.mode_of_receipt || '',
      });
    }
  }, [revenue, autoPayout]); // eslint-disable-line

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.received_amount || parseFloat(form.received_amount) <= 0) {
      toast.error('Received amount is required');
      return;
    }
    if (!form.date_of_receipt) {
      toast.error('Date of receipt is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        payout_to_company: autoPayout || Number(revenue.payout_to_company || 0),
        received_amount: parseFloat(form.received_amount),
        date_of_receipt: toISO(form.date_of_receipt),
        mode_of_receipt: form.mode_of_receipt || null,
      };
      await api.patchLeadRevenue(revenue.id, payload);
      toast.success('Revenue updated');
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const isRealised = !!(revenue.received_amount && revenue.date_of_receipt);

  return (
    <tr style={{ background: editing ? '#FAFBFC' : C.white, transition:'background 0.1s' }}
      onMouseEnter={e => { if(!editing) e.currentTarget.style.background='#F8FAFC'; }}
      onMouseLeave={e => { if(!editing) e.currentTarget.style.background=C.white; }}>

      {/* Lead */}
      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.borderL}` }}>
        <div
          onClick={() => navigate(`/leads/${revenue.lead}`)}
          style={{ fontWeight:700, fontSize:13.5, color:C.navy,
            cursor:'pointer', textDecoration:'underline', textUnderlineOffset:2, display:'inline-block' }}>
          {lead?.customer_name || `Lead #${revenue.lead}`}
        </div>
        <div style={{ fontSize:11.5, color:C.t4, marginTop:2, fontFamily:'monospace' }}>
          {lead?.lead_no || `#${revenue.lead}`}
        </div>
      </td>

      {/* Payout to Company — auto-calculated, read-only */}
      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.borderL}` }}>
        <div style={{ fontSize:13, fontWeight:700, color:C.navy }}>
          {autoPayout > 0 ? fmtAmt(autoPayout) : '—'}
        </div>
        {Number(revenue.payout_to_company || 0) === 0 && autoPayout > 0 && (
          <div style={{ fontSize:10, color:C.t4, marginTop:2, display:'flex', alignItems:'center', gap:3 }}>
            <span style={{ background:C.goldL, color:'#92400E', padding:'1px 5px',
              borderRadius:4, fontSize:9.5, fontWeight:700 }}>AUTO</span>
            Disb × Rate × Income%
          </div>
        )}
      </td>

      {/* Invoice */}
      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.borderL}` }}>
        {revenue.invoice_number ? (
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:12.5, fontWeight:600, color:C.t1 }}>
              <FiFileText size={12} style={{ color:C.teal }}/> {revenue.invoice_number}
            </div>
            <div style={{ fontSize:11.5, color:C.t4, marginTop:2 }}>
              {fmtDate(revenue.invoice_date)}
              {revenue.payout_to_company && ` · ${fmtAmt(revenue.payout_to_company)}`}
            </div>
          </div>
        ) : (
          <span style={{ fontSize:12, color:C.t4, fontStyle:'italic' }}>No invoice</span>
        )}
      </td>

      {/* Received Amount — pre-filled with autoPayout, editable */}
      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.borderL}`, minWidth:130 }}>
        {editing ? (
          <input type="number" placeholder="Amount" value={form.received_amount}
            onChange={e => set('received_amount', e.target.value)}
            style={INPUT}/>
        ) : (
          <span style={{ fontSize:13, fontWeight: isRealised ? 700 : 400,
            color: isRealised ? C.green : C.t4 }}>
            {revenue.received_amount ? fmtAmt(revenue.received_amount) : '— Not entered'}
          </span>
        )}
      </td>

      {/* Date of Receipt */}
      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.borderL}`, minWidth:130 }}>
        {editing ? (
          <DateInput 
            value={form.date_of_receipt}
            onChange={e => set('date_of_receipt', e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            style={INPUT}
          />
        ) : (
          <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:13,
            color: revenue.date_of_receipt ? C.t1 : C.t4 }}>
            {revenue.date_of_receipt ? (
              <><FiCalendar size={12} style={{ color:C.t4 }}/>{fmtDate(revenue.date_of_receipt)}</>
            ) : '— Not entered'}
          </div>
        )}
      </td>

      {/* Mode of Receipt */}
      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.borderL}`, minWidth:120 }}>
        {editing ? (
          <select value={form.mode_of_receipt} onChange={e => set('mode_of_receipt', e.target.value)}
            style={{ ...INPUT, cursor:'pointer', appearance:'none' }}>
            {MODE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          revenue.mode_of_receipt ? (
            <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px',
              borderRadius:20, fontSize:12, fontWeight:700, background:C.tealL, color:C.teal,
              border:`1px solid ${C.teal}40` }}>
              {revenue.mode_of_receipt}
            </span>
          ) : <span style={{ fontSize:12, color:C.t4, fontStyle:'italic' }}>— Not entered</span>
        )}
      </td>

      {/* Realised badge */}
      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.borderL}` }}>
        <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px',
          borderRadius:20, fontSize:11.5, fontWeight:700,
          background: isRealised ? C.greenL : C.goldL,
          color: isRealised ? C.green : '#92400E',
          border: `1px solid ${isRealised ? C.greenB : '#FDE68A'}` }}>
          {isRealised ? <><FiCheck size={10}/> Realised</> : '⏳ Pending'}
        </span>
      </td>

      {/* Actions */}
      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.borderL}` }}>
        <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
          {editing ? (
            <>
              <button onClick={handleSave} disabled={saving}
                style={{ display:'flex', alignItems:'center', gap:5,
                  height:32, padding:'0 12px', borderRadius:8,
                  background:C.teal, color:'#fff', border:'none',
                  fontSize:12.5, fontWeight:700, cursor:saving?'not-allowed':'pointer',
                  fontFamily:'inherit' }}>
                {saving ? <Spin/> : <><FiSave size={12}/> Save</>}
              </button>
              <button onClick={() => setEditing(false)} disabled={saving}
                style={{ height:32, padding:'0 10px', borderRadius:8,
                  background:C.white, color:C.t2, border:`1px solid ${C.border}`,
                  fontSize:12.5, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)}
                style={{ display:'flex', alignItems:'center', gap:4,
                  height:32, padding:'0 10px', borderRadius:8,
                  background:C.navyL, color:C.navy, border:`1px solid ${C.border}`,
                  fontSize:12.5, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                <FiEdit2 size={12}/> Edit
              </button>
              <button onClick={() => navigate(`/leads/${revenue.lead}`)}
                style={{ display:'flex', alignItems:'center', gap:4,
                  height:32, padding:'0 10px', borderRadius:8,
                  background:C.white, color:C.t2, border:`1px solid ${C.border}`,
                  fontSize:12.5, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                <FiEye size={12}/> Lead
              </button>
              {lead?.current_stage === 'COMPLETE' ? (
              <span style={{ display:'flex', alignItems:'center', gap:4,
                height:32, padding:'0 10px', borderRadius:8,
                background:C.redL, color:C.red, border:`1px solid ${C.redB}`,
                fontSize:12.5, fontWeight:600, cursor:'not-allowed' }}>
              <FiCheck size={12}/> Completed
              </span>
              ) : (
              <button onClick={async () => {
                  // R1: received_amount must be saved in DB (form pre-fill does not count)
                  if (!revenue.received_amount) {
                    toast.error('Received amount is required before completing lead');
                    return;
                  }
                  // R2: date_of_receipt must be saved in DB
                  if (!revenue.date_of_receipt) {
                    toast.error('Date of receipt is required before completing lead');
                    return;
                  }
                  // R3: invoice_number must exist in DB if invoice_required=true
                  if ((revenue.invoice_required === true || revenue.invoice_required === 'true') && !revenue.invoice_number) {
                    toast.error('Invoice number is required before completing lead');
                    return;
                  }
                  if (!window.confirm('Complete this lead? The lead will move to Completed.')) return;
                  setSaving(true);
                  try {
                    await api.completeLead(revenue.lead);
                    toast.success('Lead completed');
                    onSaved();
                  } catch (e) {
                    toast.error(e?.message || 'Failed to complete');
                  } finally { setSaving(false); }
                }}
                style={{ display:'flex', alignItems:'center', gap:4,
                  height:32, padding:'0 10px', borderRadius:8,
                  background:C.greenL, color:C.green, border:`1px solid ${C.greenB}`,
                  fontSize:12.5, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                <FiCheck size={12}/> Complete Lead
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════ */
export default function RevenueManagement() {
  const navigate = useNavigate();
  const [records,   setRecords]  = useState([]);
  const [leadsMap,  setLeadsMap] = useState({});
  const [loading,   setLoading]  = useState(true);
  const [filter,    setFilter]   = useState('ALL');
  const [search,    setSearch]   = useState('');
  const [page,      setPage]     = useState(1);
  const [total,     setTotal]    = useState(0);
  const PAGE = 20;

  const fetchData = useCallback(async (pg = 1, status = 'ALL', q = '') => {
    setLoading(true);
    try {
      const params = { page: pg, page_size: PAGE };
      if (q.trim()) params.search = q.trim();
      // Filter by has received amount
      if (status === 'PENDING')  params.has_received = 'false';
      if (status === 'RECEIVED') params.has_received = 'true';

      const res  = await api.getLeadRevenues(params);
      const data = res?.data;
      const arr  = Array.isArray(data?.results) ? data.results
                 : Array.isArray(data)           ? data : [];
      setRecords(arr);
      setTotal(data?.count || arr.length);
      setPage(pg);

      // No need to fetch lead details separately - it's included in lead_detail
      const map = {};
      arr.forEach(r => {
        if (r.lead_detail) map[r.lead] = r.lead_detail;
      });
      setLeadsMap(prev => ({ ...prev, ...map }));
    } catch (e) {
      toast.error('Failed to load revenue records');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(1, filter, search); }, []); // eslint-disable-line

  const onTabClick = (val) => { setFilter(val); fetchData(1, val, search); };
  const totalPages = Math.ceil(total / PAGE);

  /* ─── Summary counts ─── */
  const realisedCount = records.filter(r => r.received_amount && r.date_of_receipt).length;
  const pendingCount  = records.filter(r => !r.received_amount || !r.date_of_receipt).length;

  return (
    <>
      <style>{`
        @keyframes cfShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes cfSpin { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes cfFadeIn { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <Layout pageTitle="Revenue Management">
        <div style={{ display:'flex', flexDirection:'column', gap:16,
          animation:'cfFadeIn 0.2s ease both' }}>

          {/* ── HEADER ── */}
          <div style={{ background:'linear-gradient(120deg,#0D3B5E,#1A5276)', borderRadius:14,
            padding:'20px 24px', boxShadow:'0 4px 18px rgba(13,59,94,0.22)', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
              background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.55)',
              textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Admin Panel</div>
            <div style={{ fontSize:21, fontWeight:800, color:'#fff', letterSpacing:'-0.02em' }}>
              Revenue Management
            </div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.6)', marginTop:4 }}>
              Enter received amount, date & mode of receipt for each revenue record
            </div>
            {/* Summary pills */}
            <div style={{ marginTop:14, display:'flex', gap:10, flexWrap:'wrap' }}>
              {[
                { label:`${loading ? '…' : realisedCount} Realised`,  color:'#86EFAC' },
                { label:`${loading ? '…' : pendingCount} Pending`,    color:'#FDE68A' },
                { label:`${loading ? '…' : total} Total`,             color:'rgba(255,255,255,0.7)' },
              ].map(p => (
                <div key={p.label} style={{ padding:'5px 14px', borderRadius:20,
                  background:'rgba(255,255,255,0.13)', border:'1px solid rgba(255,255,255,0.22)',
                  fontSize:12.5, fontWeight:700, color:p.color }}>
                  {p.label}
                </div>
              ))}
            </div>
          </div>

          {/* ── FILTER BAR ── */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
            padding:'12px 16px', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap',
            boxShadow:'0 1px 4px rgba(13,59,94,0.05)' }}>
            <div style={{ display:'flex', gap:6 }}>
              {[
                { val:'ALL',      label:'All'           },
                { val:'PENDING',  label:'⏳ Not Received' },
                { val:'RECEIVED', label:'✓ Received'    },
              ].map(tab => {
                const on = filter === tab.val;
                const tabColor = tab.val === 'RECEIVED' ? C.green : tab.val === 'PENDING' ? C.gold : C.navy;
                return (
                  <button key={tab.val} onClick={() => onTabClick(tab.val)}
                    style={{ padding:'7px 14px', borderRadius:20, fontFamily:'inherit',
                      border:`1.5px solid ${on ? tabColor : C.border}`,
                      background: on ? tabColor : C.white,
                      color: on ? '#fff' : C.t2,
                      fontSize:12.5, fontWeight:700, cursor:'pointer', transition:'all 0.12s' }}>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div style={{ flex:1, minWidth:180, position:'relative' }}>
              <FiSearch size={13} style={{ position:'absolute', left:10, top:'50%',
                transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/>
              <input type="text" placeholder="Search lead name..."
                value={search} onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if(e.key==='Enter') fetchData(1, filter, search); }}
                style={{ width:'100%', height:38, border:`1.5px solid ${C.border}`, borderRadius:9,
                  padding:'0 12px 0 30px', fontSize:13, color:C.t1, background:'#FAFBFC',
                  outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}/>
            </div>

            <button onClick={() => fetchData(page, filter, search)} title="Refresh"
              style={{ width:38, height:38, borderRadius:9, background:C.navyL,
                border:`1px solid ${C.border}`, color:C.navy, cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center' }}>
              <FiRefreshCw size={13}/>
            </button>
          </div>

          {/* ── TABLE ── */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
            overflow:'hidden', boxShadow:'0 1px 4px rgba(13,59,94,0.05)' }}>

            {loading && <div style={{ padding:16 }}>{[1,2,3,4,5].map(i => <Sh key={i}/>)}</div>}

            {!loading && records.length === 0 && (
              <div style={{ padding:'56px 24px', textAlign:'center' }}>
                <div style={{ width:54, height:54, borderRadius:'50%', background:C.navyL,
                  display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                  <FiTrendingUp size={22} style={{ color:C.navy }}/>
                </div>
                <div style={{ fontSize:15, fontWeight:700, color:C.t1, marginBottom:6 }}>No revenue records</div>
                <div style={{ fontSize:13, color:C.t4 }}>Revenue records appear here after PDD is approved.</div>
              </div>
            )}

            {!loading && records.length > 0 && (
              <>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                    <thead>
                      <tr style={{ background:'#F7F9FB' }}>
                        {['Lead / Customer', 'Payout to Co.', 'Invoice', 'Received Amount', 'Date of Receipt', 'Mode', 'Status', 'Actions'].map((h, i) => (
                          <th key={h} style={{ padding:'10px 16px', fontSize:10.5, fontWeight:800,
                            color:C.t1, textTransform:'uppercase', letterSpacing:'0.07em',
                            textAlign: i === 7 ? 'right' : 'left',
                            borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {records.map(rev => (
                        <EditableRow
                          key={rev.id}
                          revenue={rev}
                          lead={leadsMap[rev.lead] || null}
                          onSaved={() => fetchData(page, filter, search)}
                          navigate={navigate}/>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ borderTop:`1px solid ${C.borderL}`, padding:'12px 16px',
                    display:'flex', justifyContent:'space-between', alignItems:'center',
                    background:'#FAFBFC' }}>
                    <span style={{ fontSize:12.5, color:C.t4 }}>
                      Page {page} of {totalPages} · {total} records
                    </span>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={() => fetchData(page-1, filter, search)} disabled={page<=1}
                        style={{ padding:'6px 16px', borderRadius:8, border:`1px solid ${C.border}`,
                          background:C.white, color:page<=1?C.t4:C.navy,
                          fontSize:13, fontWeight:600, cursor:page<=1?'default':'pointer',
                          fontFamily:'inherit' }}>
                        ← Prev
                      </button>
                      <button onClick={() => fetchData(page+1, filter, search)} disabled={page>=totalPages}
                        style={{ padding:'6px 16px', borderRadius:8, border:`1px solid ${C.border}`,
                          background:C.white, color:page>=totalPages?C.t4:C.navy,
                          fontSize:13, fontWeight:600, cursor:page>=totalPages?'default':'pointer',
                          fontFamily:'inherit' }}>
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </Layout>
    </>
  );
}
